<template>
  <div class="space-y-4">
    <!-- Header with sorting and refresh -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl font-bold">Top {{ limit }} Symbols</h2>
      <div class="flex items-center space-x-4">
        <select
          v-model="selectedSortBy"
          class="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="total_premium_value">Total Premium Value</option>
          <option value="price_change_percent_desc">24h Change (High to Low)</option>
          <option value="price_change_percent_asc">24h Change (Low to High)</option>
          <option value="momentum_desc">Momentum (High to Low)</option>
          <option value="momentum_asc">Momentum (Low to High)</option>
          <option value="momentum_4h">4H Momentum</option>
          <option value="momentum_1h">1H Momentum</option>
          <option value="momentum_30m">30M Momentum</option>
          <option value="stock_price">Stock Price</option>
          <option value="mid_premium">Mid Premium</option>
          <option value="momentum">Momentum</option>
          <option value="contracts_affordable">Contracts Affordable</option>
        </select>
        <select
          v-model="selectedExpiry"
          class="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">All Expirations</option>
          <option v-for="expiry in uniqueExpirations" :key="expiry" :value="expiry">
            {{ formatDateFilter(expiry) }}
          </option>
        </select>
        <select
          v-model="selectedSector"
          class="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">All Sectors</option>
          <option v-for="sector in uniqueSectors" :key="sector" :value="sector">
            {{ sector }}
          </option>
        </select>
        <button
          @click="refreshData"
          :disabled="isLoading"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
        >
          <svg
            v-if="isLoading"
            class="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Refresh
        </button>
      </div>
    </div>

    <!-- Warning banner for cached data -->
    <div
      v-if="hasCachedData"
      class="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20"
    >
      <div class="flex">
        <svg
          class="h-5 w-5 text-yellow-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            Some data is cached
          </h3>
          <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
            Due to API limitations, some data may be from cache. Click refresh to get
            fresh data.
          </p>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="space-y-3">
      <div
        v-for="i in limit"
        :key="i"
        class="h-16 animate-pulse rounded-md bg-muted"
      ></div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="rounded-md border border-destructive/50 bg-destructive/10 p-4"
    >
      <div class="flex">
        <svg
          class="h-5 w-5 text-destructive"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clip-rule="evenodd"
          />
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-destructive">Error loading data</h3>
          <p class="mt-1 text-sm text-destructive/80">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Data table -->
    <div v-else-if="filteredSymbols.length > 0" class="rounded-md border">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('symbol')"
              >
                Symbol
                <span v-if="sortField === 'symbol'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('stock_price')"
              >
                Stock Price
                <span v-if="sortField === 'stock_price'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('price_change_percent')"
              >
                24h %
                <span v-if="sortField === 'price_change_percent'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('momentum')"
              >
                3D Mom %
                <span v-if="sortField === 'momentum'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('momentum_4h')"
              >
                4H Mom %
                <span v-if="sortField === 'momentum_4h'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('momentum_1h')"
              >
                1H Mom %
                <span v-if="sortField === 'momentum_1h'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('momentum_30m')"
              >
                30M Mom %
                <span v-if="sortField === 'momentum_30m'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('strike')"
              >
                Strike
                <span v-if="sortField === 'strike'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('expiry')"
              >
                Expiry
                <span v-if="sortField === 'expiry'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('sector')"
              >
                Sector
                <span v-if="sortField === 'sector'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('mid_premium')"
              >
                Mid Premium
                <span v-if="sortField === 'mid_premium'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('contracts_affordable')"
              >
                Contracts
                <span v-if="sortField === 'contracts_affordable'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                @click="handleSort('total_premium_value')"
              >
                Total Premium
                <span v-if="sortField === 'total_premium_value'" class="ml-1">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground"
              >
                Status
              </th>
              <th
                class="h-12 px-4 text-left align-middle font-medium text-muted-foreground"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="symbol in filteredSymbols"
              :key="symbol.symbol"
              class="border-b transition-colors hover:bg-muted/50"
            >
              <td class="p-4 align-middle">
                <router-link
                  :to="`/symbol/${symbol.symbol}`"
                  class="font-medium text-primary hover:underline"
                >
                  {{ symbol.symbol }}
                </router-link>
              </td>
              <td class="p-4 align-middle">
                {{ formatCurrency(symbol.stock_price) }}
              </td>
              <td class="p-4 align-middle font-medium" :class="getPercentClass(symbol.price_change_percent)">
                {{ formatPercent(symbol.price_change_percent) }}
              </td>
              <td class="p-4 align-middle font-medium" :class="getPercentClass(symbol.momentum)">
                {{ formatPercent(symbol.momentum) }}
              </td>
              <td class="p-4 align-middle font-medium" :class="getPercentClass(symbol.momentum_4h)">
                {{ formatPercent(symbol.momentum_4h) }}
              </td>
              <td class="p-4 align-middle font-medium" :class="getPercentClass(symbol.momentum_1h)">
                {{ formatPercent(symbol.momentum_1h) }}
              </td>
              <td class="p-4 align-middle font-medium" :class="getPercentClass(symbol.momentum_30m)">
                {{ formatPercent(symbol.momentum_30m) }}
              </td>
              <td class="p-4 align-middle">
                {{ formatCurrency(symbol.strike) }}
              </td>
              <td class="p-4 align-middle">
                {{ formatDate(symbol.expiry) }}
              </td>
              <td class="p-4 align-middle">
                <span class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80">
                  {{ symbol.sector || 'Unknown' }}
                </span>
              </td>
              <td class="p-4 align-middle">
                {{ formatCurrency(symbol.mid_premium) }}
              </td>
              <td class="p-4 align-middle">
                {{ formatNumber(symbol.contracts_affordable) }}
              </td>
              <td class="p-4 align-middle font-medium">
                {{ formatCurrency(symbol.total_premium_value) }}
              </td>
              <td class="p-4 align-middle">
                <DataFreshnessIndicator
                  :is-cached="symbol.metadata.is_cached"
                  :timestamp="symbol.metadata.timestamp"
                  :age-seconds="symbol.metadata.age_seconds"
                />
              </td>
              <td class="p-4 align-middle">
                <router-link
                  :to="`/charts?symbol=${symbol.symbol}`"
                  class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-8 px-3"
                >
                  <svg
                    class="mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M3 3v18h18" />
                    <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
                  </svg>
                  Chart
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="text-center py-8">
      <p class="text-muted-foreground">No symbols found</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { apiClient, type TopSymbol } from '@/lib/api'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { useInitialCash } from '@/lib/initialCash'
import DataFreshnessIndicator from './DataFreshnessIndicator.vue'

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

interface Props {
  listType: string
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  limit: 10
})

const symbols = ref<TopSymbol[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const sortField = ref('total_premium_value')
const sortOrder = ref('desc')
const selectedExpiry = ref('')
const selectedSector = ref('')
const { initialCash } = useInitialCash()

// Computed property to sync dropdown with sort state
const selectedSortBy = computed({
  get: () => {
    if (sortField.value === 'price_change_percent') {
      return `price_change_percent_${sortOrder.value}`
    }
    if (sortField.value === 'momentum') {
      return `momentum_${sortOrder.value}`
    }
    if (['momentum_4h', 'momentum_1h', 'momentum_30m'].includes(sortField.value)) {
      return sortField.value
    }
    return sortField.value
  },
  set: (newValue: string) => {
    if (newValue === 'price_change_percent_asc') {
      sortField.value = 'price_change_percent'
      sortOrder.value = 'asc'
    } else if (newValue === 'price_change_percent_desc') {
      sortField.value = 'price_change_percent'
      sortOrder.value = 'desc'
    } else if (newValue === 'momentum_asc') {
      sortField.value = 'momentum'
      sortOrder.value = 'asc'
    } else if (newValue === 'momentum_desc') {
      sortField.value = 'momentum'
      sortOrder.value = 'desc'
    } else if (['momentum_4h', 'momentum_1h', 'momentum_30m'].includes(newValue)) {
      sortField.value = newValue
      sortOrder.value = 'desc'
    } else {
      sortField.value = newValue
      sortOrder.value = 'desc' // Default to desc for other fields
    }
  }
})

const handleSort = (field: string) => {
  if (sortField.value === field) {
    // Toggle order
    sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
  } else {
    // New field, default to desc
    sortField.value = field
    sortOrder.value = 'desc'
  }
}

const uniqueExpirations = computed(() => {
  const expiries = symbols.value.map((s: TopSymbol) => s.expiry)
  return [...new Set(expiries)].sort()
})

const uniqueSectors = computed(() => {
  const sectors = symbols.value
    .map((s: TopSymbol) => s.sector)
    .filter((s: string | undefined): s is string => !!s && s !== 'Unknown')
  return [...new Set(sectors)].sort()
})

const filteredSymbols = computed(() => {
  let result = symbols.value

  if (selectedExpiry.value) {
    result = result.filter((symbol: TopSymbol) => symbol.expiry === selectedExpiry.value)
  }

  if (selectedSector.value) {
    result = result.filter((symbol: TopSymbol) => symbol.sector === selectedSector.value)
  }

  return result
})

const formatDateFilter = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return '-'
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
}

const getPercentClass = (value?: number): string => {
  if (value === undefined || value === null) return 'text-muted-foreground'
  return value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-muted-foreground'
}

const hasCachedData = computed(() => {
  return symbols.value.some((symbol: TopSymbol) => symbol.metadata.is_cached)
})

const fetchData = async (forceRefresh: boolean = false) => {
  isLoading.value = true
  error.value = null

  try {
    const data = await apiClient.getTopSymbols(
      props.listType,
      props.limit,
      sortField.value,
      initialCash.value,
      forceRefresh,
      sortOrder.value
    )
    symbols.value = data
  } catch (err: any) {
    error.value = err.response?.data?.detail || err.message || 'Failed to fetch data'
    symbols.value = []
  } finally {
    isLoading.value = false
  }
}

const refreshData = () => {
  fetchData(true) // Pass true to force cache override
}

// Watch for changes in sort order
watch([sortField, sortOrder], () => {
  fetchData()
})

// Watch for changes in list type
watch(() => props.listType, () => {
  fetchData()
})

// Reset expiry filter when data changes
watch(symbols, () => {
  // Reset filter if selected expiry no longer exists
  if (selectedExpiry.value && !uniqueExpirations.value.includes(selectedExpiry.value)) {
    selectedExpiry.value = ''
  }
  // Reset sector filter if selected sector no longer exists
  if (selectedSector.value && !uniqueSectors.value.includes(selectedSector.value)) {
    selectedSector.value = ''
  }
})

// Watch for changes in initial cash
watch(initialCash, () => {
  fetchData()
})

onMounted(() => {
  fetchData()
})
</script>
